import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
  publicQueueApi,
  queueDisplayApi,
  type MultiRoomDisplay,
  type QueueDisplay,
  type QueueEntry,
  type RoomQueue,
} from "../../shared/api/queueDisplay";

/**
 * Waiting-room TV queue. Designed for a 1080p kiosk display, large fonts,
 * auto-refresh every 10 seconds. The route is intentionally NOT inside the
 * AppShell — no sidebar/topbar, full-bleed dark background, so the TV view
 * doesn't look like a regular staff page.
 *
 * <p>Two URL shapes:
 *
 * <ul>
 *   <li>{@code /display/queue?token=<opaque>} — kiosk mode for unattended TVs.
 *       Calls the unauthenticated /api/v1/public/queue/display endpoint.
 *   <li>{@code /display/queue?branchId=<uuid>} — staff mode from a logged-in
 *       machine. Uses the JWT-bearing client + branch-scoped endpoint.
 * </ul>
 *
 * <p>Optional {@code date=YYYY-MM-DD} pins a specific day; default is "today"
 * in {@code Asia/Ho_Chi_Minh}.
 */
export function QueueDisplayPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const branchId = params.get("branchId") ?? "";
  const date = params.get("date");
  const room = params.get("room"); // per-room display when present
  // Display layout (design §7/§11). A = single current number · B = current + next (default) ·
  // C = current + preparation group. Later moves onto a display profile/device; URL for now.
  const layout = (params.get("layout") ?? "B").toUpperCase() as DisplayLayout;
  // Số bệnh nhân "Mời chuẩn bị" cho layout C. URL ?prep= là override thiết bị; nếu không có thì
  // dùng cấu hình phòng (data.prepareGroupSize).
  const prepOverride = params.get("prep");

  // Token mode wins when both are present — it's the more constrained surface.
  const mode: "token" | "staff" | "missing" =
    token ? "token" : branchId ? "staff" : "missing";

  const isMulti = layout === "D"; // shared multi-room waiting-area board (View D)

  const q = useQuery({
    queryKey: ["queue-display", mode, token || branchId, date, room],
    queryFn: () =>
      mode === "token"
        ? publicQueueApi.fetch(token, date, room)
        : queueDisplayApi.fetch(branchId, date, room),
    enabled: mode !== "missing" && !isMulti,
    // 10s refetch — close enough to feel "live" without hammering the API.
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  const multiQ = useQuery({
    queryKey: ["queue-multi", mode, token || branchId, date],
    queryFn: () =>
      mode === "token"
        ? publicQueueApi.fetchMulti(token, date)
        : queueDisplayApi.fetchMulti(branchId, date),
    enabled: mode !== "missing" && isMulti,
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
  });

  // Force the body to a dark kiosk background while this route is mounted.
  useEffect(() => {
    document.body.style.background = "#0b0f1a";
    document.body.style.color = "#f5f7fb";
    return () => {
      document.body.style.background = "";
      document.body.style.color = "";
    };
  }, []);

  // Realtime (design §8/§13): open an SSE stream and refetch on any queue-changed event —
  // near-instant, multi-instance-safe (Redis fan-out server-side). Polling stays as the fallback
  // floor; on (re)connect we refetch to recover missed events (case 9). Kiosk (token) uses native
  // EventSource on the public endpoint; staff (branchId) uses fetch-event-source so the JWT +
  // tenant/branch headers can be attached (EventSource can't set headers).
  const qc = useQueryClient();
  useEffect(() => {
    if (mode === "missing") return;
    const base =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";
    const refetch = () => {
      qc.invalidateQueries({ queryKey: ["queue-display"] });
      qc.invalidateQueries({ queryKey: ["queue-multi"] });
    };

    if (mode === "token") {
      const es = new EventSource(
        `${base}/v1/public/queue/stream?token=${encodeURIComponent(token)}`
      );
      es.addEventListener("queue-changed", refetch);
      es.addEventListener("connected", refetch);
      return () => es.close();
    }

    // Staff mode — JWT-authenticated stream.
    const ctrl = new AbortController();
    fetchEventSource(
      `${base}/v1/queue/stream?branchId=${encodeURIComponent(branchId)}`,
      {
        signal: ctrl.signal,
        openWhenHidden: true, // a TV display must keep streaming when the tab is backgrounded
        headers: {
          Authorization: `Bearer ${localStorage.getItem("addy.accessToken") ?? ""}`,
          "X-Addy-Tenant-Id": localStorage.getItem("addy.tenantId") ?? "",
          "X-Addy-Branch-Id": localStorage.getItem("addy.branchId") ?? "",
        },
        onmessage(ev: { event?: string }) {
          if (ev.event === "queue-changed" || ev.event === "connected") refetch();
        },
        onerror() {
          /* let it retry; poll fallback covers the gap */
        },
      }
    ).catch(() => {
      /* aborted on unmount */
    });
    return () => ctrl.abort();
  }, [mode, token, branchId, qc]);

  if (mode === "missing") {
    return (
      <Centered>
        <div className="text-3xl">
          Thiếu tham số <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono">token</code> hoặc{" "}
          <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono">branchId</code> trên URL.
        </div>
      </Centered>
    );
  }
  const active = isMulti ? multiQ : q;
  if (active.isLoading) {
    return <Centered>Đang tải hàng đợi…</Centered>;
  }
  if (active.isError || !active.data) {
    return <Centered>Không tải được dữ liệu.</Centered>;
  }

  if (isMulti) {
    return <MultiRoomView data={multiQ.data as MultiRoomDisplay} />;
  }
  const single = q.data as QueueDisplay;
  const prep =
    prepOverride != null
      ? Math.max(1, Number.parseInt(prepOverride, 10) || 5)
      : Math.max(1, single.prepareGroupSize || 5);
  return (
    <QueueDisplayView data={single} roomMode={!!room} layout={layout} prep={prep} />
  );
}

type DisplayLayout = "A" | "B" | "C" | "D";

/** In per-room mode the patient-facing number is the room's own sequence (roomNo);
 *  branch-wide mode keeps the day queue number. */
function numberOf(e: QueueEntry, roomMode: boolean): number {
  if (roomMode) return e.roomNo ?? e.queueNumber;
  return e.queueNumber;
}

/** Live wall-clock, updated every second for the header time. */
function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const SERVING_COLOR = "#4fe3da"; // teal — currently being examined
const CALLED_COLOR = "#7aa2ff"; // blue — called / invited in
const WAITING_COLOR = "#ffcf5c"; // amber — waiting
const DONE_COLOR = "#8b95a8"; // neutral gray — completed

function QueueDisplayView({
  data,
  roomMode,
  layout = "B",
  prep = 5,
}: {
  data: QueueDisplay;
  roomMode: boolean;
  layout?: DisplayLayout;
  prep?: number;
}) {
  const { counts, entries } = data;
  const serving = useMemo(() => entries.filter((e) => e.status === "SERVING"), [entries]);
  const called = useMemo(() => entries.filter((e) => e.status === "CALLED"), [entries]);
  const waiting = useMemo(() => entries.filter((e) => e.status === "WAITING"), [entries]);
  // Tên phòng lấy từ cấu hình (data.roomName) — đúng cả khi phòng chưa có bệnh nhân nào.
  // Fallback về roomName trên entry rồi mới tới nhãn mặc định, cho các payload cũ (#18572).
  const roomName = roomMode
    ? data.roomName ?? entries.find((e) => e.roomName)?.roomName ?? "Phòng khám"
    : null;
  const empty = serving.length === 0 && called.length === 0 && waiting.length === 0;

  // Layout A (single current number) + C (current + preparation group) are focused single-room
  // views (design §7/§10/§11). B keeps the full waiting-room board below.
  if (layout === "A" || layout === "C") {
    return (
      <FocusedRoomLayout
        layout={layout}
        prep={prep}
        roomMode={roomMode}
        roomName={roomName}
        serving={serving}
        called={called}
        waiting={waiting}
      />
    );
  }

  return (
    <div className="min-h-screen p-6 font-sans text-[#f5f7fb] sm:p-10">
      <Header date={data.date} counts={counts} roomName={roomName} />

      {empty ? (
        <div className="mt-24 flex flex-col items-center justify-center gap-4 text-center">
          <div className="text-4xl font-semibold text-[#9da7bd] sm:text-5xl">
            Hiện chưa có bệnh nhân trong hàng đợi.
          </div>
          <div className="text-2xl text-[#7c869c]">Danh sách sẽ được cập nhật tự động.</div>
        </div>
      ) : (
        // ĐANG KHÁM (dominant, left) + [ĐANG MỜI VÀO + CHỜ KHÁM] on the right of a wide TV.
        <div className="mt-8 grid gap-10 xl:grid-cols-[1.1fr_1fr]">
          <section>
            <SectionTitle text="ĐANG KHÁM" color={SERVING_COLOR} count={serving.length} />
            {serving.length === 0 ? (
              <EmptyPanel text="Chưa có bệnh nhân đang khám." />
            ) : (
              <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]">
                {serving.map((e) => (
                  <ServingCard
                    key={`${e.queueNumber}-${e.patientCode}`}
                    entry={e}
                    roomMode={roomMode}
                  />
                ))}
              </div>
            )}
          </section>

          <div className="grid gap-8">
            {called.length > 0 && (
              <section>
                <SectionTitle text="ĐANG MỜI VÀO" color={CALLED_COLOR} count={called.length} />
                <div className="grid gap-3">
                  {called.map((e) => (
                    <CalledBanner
                      key={`${e.queueNumber}-${e.patientCode}`}
                      entry={e}
                      roomMode={roomMode}
                    />
                  ))}
                </div>
              </section>
            )}

            <section>
              <SectionTitle text="CHỜ KHÁM" color={WAITING_COLOR} count={waiting.length} />
              {waiting.length === 0 ? (
                <EmptyPanel text="Hiện chưa có bệnh nhân chờ khám." />
              ) : (
                <WaitingList entries={waiting} roomMode={roomMode} />
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Focused single-room display (design §7/§10/§11): layout A = one big current number, layout C =
 * current + a "Mời chuẩn bị" group of the next N *actual* waiting STT (non-contiguous — never a
 * range, §6). Readable from a distance; STT + room only (privacy §18).
 */
function FocusedRoomLayout({
  layout,
  prep,
  roomMode,
  roomName,
  serving,
  called,
  waiting,
}: {
  layout: DisplayLayout;
  prep: number;
  roomMode: boolean;
  roomName: string | null;
  serving: QueueEntry[];
  called: QueueEntry[];
  waiting: QueueEntry[];
}) {
  // Current = the patient the room is focused on: being examined first, else being called.
  const current = serving[0] ?? called[0] ?? null;
  const currentLabel = layout === "A" ? "ĐANG GỌI" : "ĐANG KHÁM";
  const prepList = waiting.slice(0, prep); // actual next-N waiting rows (may be non-contiguous)

  return (
    <div className="flex min-h-screen flex-col p-6 sm:p-10">
      <div className="truncate text-center text-5xl font-black tracking-tight sm:text-7xl">
        {roomName ?? "Phòng khám"}
      </div>
      <div className="mt-6 flex flex-1 flex-col items-center justify-center gap-10">
        <div className="text-center">
          <div className="text-3xl font-bold tracking-widest text-[#4fe3da] sm:text-4xl">
            {currentLabel}
          </div>
          <div className="mt-1 font-black leading-none text-[#4fe3da] text-[140px] sm:text-[220px]">
            {current ? pad(numberOf(current, roomMode)) : "—"}
          </div>
        </div>
        {layout === "C" && (
          <div className="text-center">
            <div className="text-2xl font-bold tracking-wide text-[#ffcf5c] sm:text-3xl">
              MỜI CHUẨN BỊ
            </div>
            {prepList.length === 0 ? (
              <div className="mt-2 text-2xl text-[#7c869c]">Chưa có bệnh nhân chờ.</div>
            ) : (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 font-black tabular-nums text-[#ffe28a] text-5xl sm:text-6xl">
                {prepList.map((e) => (
                  <span key={`${e.queueNumber}-${e.patientCode}`}>
                    {pad(numberOf(e, roomMode))}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** "Mời STT NNN đến <phòng>" — the prominent call banner (design §9). */
function CalledBanner({ entry, roomMode }: { entry: QueueEntry; roomMode: boolean }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border-2 border-[#7aa2ff] bg-[#16203a] px-6 py-4">
      <div className="text-[64px] font-black leading-none text-[#9db8ff] sm:text-[80px]">
        {pad(numberOf(entry, roomMode))}
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold uppercase tracking-wide text-[#9db8ff]">Mời vào</div>
        <div className="truncate text-2xl font-semibold">{entry.patientNameMasked}</div>
        <div className="truncate text-lg text-[#9da7bd]">
          {entry.roomName ? entry.roomName : `BS ${entry.doctorName || "—"}`}
        </div>
      </div>
    </div>
  );
}

/** Section heading = a colour dot + an always-present Vietnamese label + count.
 *  Status is conveyed by text + shape, never colour alone (accessibility). */
function SectionTitle({ text, color, count }: { text: string; color: string; count: number }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span
        className="inline-block h-4 w-4 shrink-0 rounded-full"
        style={{ background: color }}
        aria-hidden="true"
      />
      <h2 className="text-2xl font-bold tracking-wide sm:text-3xl" style={{ color }}>
        {text}
      </h2>
      <span className="rounded-full bg-white/10 px-3 py-0.5 text-xl font-semibold tabular-nums">
        {count}
      </span>
    </div>
  );
}

function EmptyPanel({ text }: { text: string }) {
  return (
    <div className="rounded-2xl bg-white/5 p-8 text-center text-2xl text-[#7c869c]">{text}</div>
  );
}

function Header({
  date,
  counts,
  roomName,
}: {
  date: string;
  counts: QueueDisplay["counts"];
  roomName?: string | null;
}) {
  const now = useClock();
  return (
    <header className="flex flex-col gap-6 border-b border-[#1f2533] pb-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="min-w-0">
        {/* Room name is the most prominent element on the screen. */}
        <div className="truncate text-5xl font-black tracking-tight sm:text-6xl">
          {roomName ? roomName : "Hàng đợi khám bệnh"}
        </div>
        <div className="mt-2 text-2xl text-[#9da7bd]">
          {new Date(date).toLocaleDateString("vi-VN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          {" · "}
          <span className="font-mono tabular-nums text-[#f5f7fb]">
            {now.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 gap-4 sm:gap-6">
        <Pill label="Đang chờ" value={counts.waiting} color={WAITING_COLOR} />
        <Pill label="Đang gọi" value={counts.called} color={CALLED_COLOR} />
        <Pill label="Đang khám" value={counts.serving} color={SERVING_COLOR} />
        <Pill label="Đã xong" value={counts.done} color={DONE_COLOR} />
      </div>
    </header>
  );
}

function Pill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-2xl bg-white/5 px-5 py-3 text-center">
      <div className="text-base text-[#9da7bd] sm:text-lg">{label}</div>
      <div className="text-4xl font-extrabold tabular-nums sm:text-5xl" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function ServingCard({ entry, roomMode }: { entry: QueueEntry; roomMode: boolean }) {
  return (
    <div className="rounded-3xl border-2 border-[#2b6f6b] bg-[linear-gradient(180deg,#123433_0%,#0e2321_100%)] p-6 sm:p-7">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[88px] font-black leading-none text-[#4fe3da] sm:text-[120px]">
          {pad(numberOf(entry, roomMode))}
        </div>
        <div className="text-right text-base text-[#9db3b1]">
          <div className="font-mono">{entry.patientCode}</div>
          {entry.registrationNo != null && <div className="text-sm">ĐK #{entry.registrationNo}</div>}
        </div>
      </div>
      <div className="mt-2 truncate text-3xl font-bold sm:text-4xl">{entry.patientNameMasked}</div>
      <div className="mt-2 truncate text-xl text-[#9db3b1]">
        BS {entry.doctorName || "—"}
        {entry.serviceName ? ` · ${entry.serviceName}` : ""}
      </div>
    </div>
  );
}

function WaitingList({ entries, roomMode }: { entries: QueueEntry[]; roomMode: boolean }) {
  // Overflow-safe: cap the list and summarise the rest (no fast auto-scroll).
  const MAX = 10;
  const visible = entries.slice(0, MAX);
  return (
    <div className="grid gap-3">
      {visible.map((e, i) => {
        const next = i === 0; // first in queue → "TIẾP THEO"
        return (
          <div
            key={`${e.queueNumber}-${e.patientCode}`}
            className={`grid grid-cols-[auto_1fr_auto] items-center gap-4 rounded-2xl px-5 py-4 ${
              next ? "border-2 border-[#ffcf5c] bg-[#3a2f12]" : "bg-white/5"
            }`}
          >
            <div
              className={`font-black tabular-nums ${
                next ? "text-[52px] text-[#ffcf5c] sm:text-[64px]" : "text-4xl text-[#ffe28a]"
              }`}
            >
              #{pad(numberOf(e, roomMode))}
            </div>
            <div className="min-w-0">
              {next && (
                <span className="mb-1 inline-block rounded-full bg-[#ffcf5c] px-3 py-0.5 text-sm font-bold uppercase tracking-wide text-[#241a02]">
                  Tiếp theo
                </span>
              )}
              <div className="truncate text-2xl font-semibold sm:text-3xl">
                {e.patientNameMasked}
              </div>
              <div className="truncate text-lg text-[#9da7bd]">BS {e.doctorName || "—"}</div>
            </div>
            <div className="text-right text-lg tabular-nums text-[#9da7bd]">
              {new Date(e.startAt).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        );
      })}
      {entries.length > MAX && (
        <div className="mt-1 text-center text-xl text-[#7c869c]">
          … và {entries.length - MAX} bệnh nhân khác đang chờ
        </div>
      )}
    </div>
  );
}

/**
 * View D — shared multi-room waiting-area board (design §9). One TV for the whole area: each room
 * calls independently, shown as a row {Đang khám · Đang mời · Tiếp theo}. STT + room only (§18).
 */
function MultiRoomView({ data }: { data: MultiRoomDisplay }) {
  const now = useClock();
  const rooms = data.rooms;

  // Transient "Mời STT NNN đến <phòng>" banner (design §9): fire when a room's called number
  // changes to a new value. First population seeds the baseline (no banner storm on mount).
  const prevCalled = useRef<Map<string, number>>(new Map());
  const seeded = useRef(false);
  const [banner, setBanner] = useState<{ room: string; num: number } | null>(null);
  useEffect(() => {
    const prev = prevCalled.current;
    let latest: { room: string; num: number } | null = null;
    for (const r of rooms) {
      if (r.called != null && prev.get(r.roomId) !== r.called) {
        latest = { room: r.roomName ?? "Phòng khám", num: r.called };
      }
      if (r.called != null) prev.set(r.roomId, r.called);
      else prev.delete(r.roomId);
    }
    if (!seeded.current) {
      seeded.current = true; // baseline only — don't announce existing calls on first load
      return;
    }
    if (latest) setBanner(latest);
  }, [rooms]);
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 7000);
    return () => clearTimeout(t);
  }, [banner]);

  return (
    <div className="min-h-screen p-6 font-sans text-[#f5f7fb] sm:p-10">
      {banner && (
        <div className="fixed inset-x-0 top-0 z-50 animate-pulse bg-[#7aa2ff] px-6 py-5 text-center text-[#0b0f1a] shadow-2xl">
          <span className="text-3xl font-black sm:text-5xl">
            Mời STT {pad(banner.num)} đến {banner.room}
          </span>
        </div>
      )}
      <header className="flex flex-col gap-4 border-b border-[#1f2533] pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="text-5xl font-black tracking-tight sm:text-6xl">Khu chờ khám</div>
        <div className="text-2xl text-[#9da7bd]">
          {new Date(data.date).toLocaleDateString("vi-VN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
          {" · "}
          <span className="font-mono tabular-nums text-[#f5f7fb]">
            {now.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      </header>

      {rooms.length === 0 ? (
        <div className="mt-24 text-center text-4xl text-[#9da7bd]">
          Hiện chưa có phòng nào đang hoạt động.
        </div>
      ) : (
        <div className="mt-8 overflow-hidden rounded-2xl border border-[#1f2533]">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/5 text-xl uppercase tracking-wide text-[#9da7bd] sm:text-2xl">
                <th className="px-6 py-4">Phòng</th>
                <th className="px-6 py-4 text-right">Đang khám</th>
                <th className="px-6 py-4 text-right">Đang mời</th>
                <th className="px-6 py-4">Tiếp theo</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <RoomRow key={r.roomId} r={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RoomRow({ r }: { r: RoomQueue }) {
  return (
    <tr className="border-t border-[#1f2533] text-2xl sm:text-3xl">
      <td className="max-w-[16ch] truncate px-6 py-5 font-bold">
        {r.roomName ?? "Phòng khám"}
      </td>
      <td className="px-6 py-5 text-right font-black tabular-nums text-[#4fe3da]">
        {r.serving != null ? pad(r.serving) : "—"}
      </td>
      <td className="px-6 py-5 text-right font-black tabular-nums text-[#9db8ff]">
        {r.called != null ? pad(r.called) : "—"}
      </td>
      <td className="px-6 py-5 tabular-nums text-[#ffe28a]">
        {r.next.length ? r.next.map((n) => pad(n)).join(" · ") : "—"}
      </td>
    </tr>
  );
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0f1a] p-6 text-center text-2xl text-[#f5f7fb]">
      {children}
    </div>
  );
}
